import { api } from './apiClient';
import type { Patient, ClinicalCycle, ClinicalRecord } from '../types';

/** Resposta da API: paciente com cycles e selectedIndicatorIds */
export type PatientResponse = Patient;

export type CreatePatientPayload = {
  name: string;
  dob?: string;
  phone?: string;
  email?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  cpf?: string;
  cep?: string;
  address?: string;
  motherName?: string;
  fatherName?: string;
  mainComplaint?: string;
  medications?: string;
  activeApproach: string;
  cycleFrequency: string;
  selectedIndicatorIds?: string[];
  status?: PatientApiStatus;
  assignedProfessionalId?: string;
};

/** Enum persistido no banco (Prisma `PatientStatus`). */
export type PatientApiStatus = 'ATIVO' | 'INATIVO';

/** Rótulo usado na UI (`Patient['status']`). */
export type PatientUiStatus = 'Ativo' | 'Inativo';

export const toApiStatus = (status: PatientUiStatus): PatientApiStatus =>
  status === 'Inativo' ? 'INATIVO' : 'ATIVO';

export type UpdatePatientPayload = Partial<CreatePatientPayload>;

export function fetchPatients(status?: PatientApiStatus): Promise<PatientResponse[]> {
  const query = status ? `?status=${status}` : '';
  return api.get<PatientResponse[]>(`/patients${query}`);
}

export function fetchPatient(id: string): Promise<PatientResponse> {
  return api.get<PatientResponse>(`/patients/${id}`);
}

export function createPatient(payload: CreatePatientPayload): Promise<PatientResponse> {
  return api.post<PatientResponse>('/patients', payload);
}

export function updatePatient(id: string, payload: UpdatePatientPayload): Promise<PatientResponse> {
  return api.patch<PatientResponse>(`/patients/${id}`, payload);
}

export function deletePatient(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/patients/${id}`);
}

// --- Prontuário (registros de evolução) ----------------------------------

export type ClinicalRecordPayload = {
  date: string;
  content: string;
  approach?: string;
  aiAnalysis?: string;
};

/** Cria um registro de prontuário; o ID definitivo vem da API. */
export function createClinicalRecord(
  patientId: string,
  payload: ClinicalRecordPayload,
): Promise<ClinicalRecord> {
  return api.post<ClinicalRecord>(`/patients/${patientId}/records`, payload);
}

export function updateClinicalRecord(
  patientId: string,
  recordId: string,
  payload: Partial<ClinicalRecordPayload>,
): Promise<ClinicalRecord> {
  return api.patch<ClinicalRecord>(`/patients/${patientId}/records/${recordId}`, payload);
}

export function deleteClinicalRecord(
  patientId: string,
  recordId: string,
): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/patients/${patientId}/records/${recordId}`);
}

/** Arquiva (inativa) ou reativa pacientes em lote. */
export function setPatientsStatus(
  ids: string[],
  status: PatientUiStatus,
): Promise<{ success: boolean; updated: number; status: PatientApiStatus }> {
  return api.patch('/patients/status', { ids, status: toApiStatus(status) });
}

/** Bloco #75: sessão do histórico de frequência (agenda interna ou link externo). */
export type PatientSession = {
  id: string;
  source: 'agenda' | 'portal';
  date: string;
  start: string;
  end: string;
  modality: string | null;
  locationName: string | null;
  professionalName: string | null;
  serviceName: string | null;
  serviceValue: string | null;
  attendance: string | null;
  canceled: boolean;
  confirmed: boolean;
  recurrence: string | null;
};

export function fetchPatientSessions(id: string): Promise<PatientSession[]> {
  return api.get<PatientSession[]>(`/patients/${encodeURIComponent(id)}/sessions`);
}
